import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader2, RefreshCw, X } from 'lucide-react';
import { PageHeader, Card, Button } from '../components/ui';
import { OverviewStatCards } from '../components/overview/OverviewStatCards';
import { ReputationSection } from '../components/overview/ReputationSection';
import { CompetitorsSection } from '../components/overview/CompetitorsSection';
import { MetrikaSection } from '../components/overview/MetrikaSection';
import {
  businessApi,
  reviewsApi,
  competitorApi,
  yandexApi,
  type BusinessDetail,
  type ReputationPeriod,
  type ReputationTrendPoint,
  type CompetitorPositionHistoryPoint,
  type YandexCompetitorPositionsData,
  type KeywordPositionsResult,
  type MetrikaPrefillData,
  type MetrikaDailyTrendPoint,
} from '../lib/api';

function periodToDateRange(period: ReputationPeriod): { dateFrom: string; dateTo: string } {
  const days = { '7d': 7, '30d': 30, '90d': 90, year: 365 }[period];
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { dateFrom: from.toISOString().slice(0, 10), dateTo: to.toISOString().slice(0, 10) };
}

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<BusinessDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [period, setPeriod] = useState<ReputationPeriod>('30d');
  const [reputationTrend, setReputationTrend] = useState<ReputationTrendPoint[]>([]);
  const [reputationLoading, setReputationLoading] = useState(true);

  const [weekTrend, setWeekTrend] = useState<ReputationTrendPoint[]>([]);

  const [competitorData, setCompetitorData] = useState<YandexCompetitorPositionsData | null>(null);
  const [positionHistory, setPositionHistory] = useState<CompetitorPositionHistoryPoint[]>([]);
  const [keywordPositions, setKeywordPositions] = useState<KeywordPositionsResult | null>(null);
  const [competitorLoading, setCompetitorLoading] = useState(true);

  const [metrikaConnected, setMetrikaConnected] = useState(false);
  const [metrikaMetrics, setMetrikaMetrics] = useState<MetrikaPrefillData | null>(null);
  const [metrikaTrend, setMetrikaTrend] = useState<MetrikaDailyTrendPoint[]>([]);
  const [metrikaLoading, setMetrikaLoading] = useState(true);

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    businessApi
      .getDetail(id)
      .then((res) => setData(res.data.data))
      .catch((e) => {
        console.error(e);
        setError('Не удалось загрузить данные клиента');
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Reputation trend — reacts to the period switcher.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setReputationLoading(true);
    reviewsApi
      .getReputationTrend(id, period)
      .then((res) => {
        if (!cancelled) setReputationTrend(res.data.data);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (!cancelled) setReputationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, period]);

  // Fixed 7-day (this week vs. prior week) trend for the stat-card sparklines/deltas.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    reviewsApi
      .getReputationTrend(id, '7d')
      .then((res) => {
        if (!cancelled) setWeekTrend(res.data.data);
      })
      .catch((e) => console.error(e));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Competitor position — current snapshot + history, reacts to the period switcher.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setCompetitorLoading(true);
    Promise.all([
      yandexApi.getCompetitorPositions(id, {}).catch(() => null),
      competitorApi.getPositionHistory(id, period).catch(() => ({ data: { data: [] } })),
      competitorApi.getKeywordPositions(id).catch(() => null),
    ])
      .then(([positions, history, keywordPos]) => {
        if (cancelled) return;
        if (positions) setCompetitorData(positions.data.data);
        setPositionHistory(history.data.data);
        setKeywordPositions(keywordPos ? keywordPos.data.data : null);
      })
      .finally(() => {
        if (!cancelled) setCompetitorLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, period]);

  // Metrika — connection status, then metrics + daily trend if connected.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setMetrikaLoading(true);
    yandexApi
      .getStatus(id)
      .then((statusRes) => {
        if (cancelled) return;
        const connected = statusRes.data.data.connected;
        setMetrikaConnected(connected);
        if (!connected) {
          setMetrikaLoading(false);
          return;
        }
        const { dateFrom, dateTo } = periodToDateRange(period);
        return Promise.all([
          yandexApi.getBusinessAnalytics(id, { dateFrom, dateTo, saveCounter: false }).catch(() => null),
          yandexApi.getMetrikaDailyTrend(id, { dateFrom, dateTo }).catch(() => null),
        ]).then(([analytics, trend]) => {
          if (cancelled) return;
          if (analytics) setMetrikaMetrics(analytics.data.data.metrics);
          if (trend) setMetrikaTrend(trend.data.data);
          setMetrikaLoading(false);
        });
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          setMetrikaConnected(false);
          setMetrikaLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, period]);

  const handleSync = async () => {
    if (!id) return;
    setIsSyncing(true);
    setSyncMessage(null);
    try {
      const res = await reviewsApi.sync(id);
      setSyncMessage({ text: res.data.data.message, type: 'success' });
      const [detailRes, trendRes, weekRes] = await Promise.all([
        businessApi.getDetail(id),
        reviewsApi.getReputationTrend(id, period),
        reviewsApi.getReputationTrend(id, '7d'),
      ]);
      setData(detailRes.data.data);
      setReputationTrend(trendRes.data.data);
      setWeekTrend(weekRes.data.data);
    } catch {
      setSyncMessage({ text: 'Не удалось синхронизировать отзывы', type: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  const statsLoading = reputationLoading || competitorLoading || metrikaLoading;

  const weekAvgRating = useMemo(() => {
    if (weekTrend.length === 0) return 0;
    const totalWeighted = weekTrend.reduce((sum, p) => sum + p.avg * p.count, 0);
    const totalCount = weekTrend.reduce((sum, p) => sum + p.count, 0);
    return totalCount > 0 ? totalWeighted / totalCount : 0;
  }, [weekTrend]);

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Card className="flex items-center gap-3 border-red-200 bg-red-50 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error || 'Клиент не найден'}</span>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={data.name}
        subtitle={`${data.category} • ${data.city}`}
        actions={
          data.yandexMapsUrl && (
            <Button
              variant="outline"
              size="sm"
              icon={isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              onClick={handleSync}
              loading={isSyncing}
              title="Синхронизация отзывов с Яндекс.Карт"
            >
              Синхронизация
            </Button>
          )
        }
      />

      <div className="p-6 space-y-6">
        {syncMessage && (
          <div
            className={`p-4 rounded-lg border flex items-center gap-2 ${
              syncMessage.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-red-50 border-red-200 text-red-700'
            }`}
          >
            {syncMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="flex-1">{syncMessage.text}</span>
            <button onClick={() => setSyncMessage(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <OverviewStatCards
          loading={statsLoading}
          rating={{
            value: weekAvgRating,
            sparkline: weekTrend.map((p) => ({ value: p.avg })),
          }}
          totalReviews={{
            value: data.reviewsCount,
          }}
          unansweredReviews={{
            value: data.newReviewsCount,
          }}
          competitorPosition={{
            value: keywordPositions?.averagePosition ?? competitorData?.ownPosition ?? null,
            sparkline: positionHistory.filter((p) => p.position !== null).map((p) => ({ value: p.position as number })),
          }}
          visits={{
            value: metrikaMetrics?.websiteClicks ?? 0,
            sparkline: metrikaTrend.map((p) => ({ value: p.visits })),
          }}
        />

        <ReputationSection
          period={period}
          onPeriodChange={setPeriod}
          trend={reputationTrend}
          loading={reputationLoading}
        />

        <CompetitorsSection
          businessId={data.id}
          businessName={data.name}
          data={competitorData}
          history={positionHistory}
          keywordPositions={keywordPositions}
          loading={competitorLoading}
        />

        <MetrikaSection
          connected={metrikaConnected}
          onConnect={() => navigate(`/client/${data.id}/analytics`)}
          metrics={metrikaMetrics}
          trend={metrikaTrend}
          loading={metrikaLoading}
        />
      </div>
    </div>
  );
}
